// ./src/components/features/Artists/EventArtistDatesEditor/EventArtistDatesEditor.tsx
//
// "Dates & set times" in the booking drawer (ROADMAP A2h #165 round 2, owner 2026-10-16: "can we also edit time slots from
// event's artists page … I also think that act can have reference to Map element as well"). One row per date (or session)
// the act plays: which date, when its set starts and ends on it (in the EVENT's zone — empty = the date's own time), and
// the stage on the event map (empty = the date's own). The rules of the times are `performanceRules.ts`.
//
// ⓘ The same act on another date with OTHER terms (billing, photo, position, fee) is another booking — copy the date's
//   lineup from the Artists page. One booking on several dates here plays them all on the same terms.

import React from "react";
import { ActionIcon, Button, Group, Input, Paper, Stack, Text } from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { formatEventDateTimeRange } from "@em/utils";

import { EventTimeSlot } from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";

import { PerformanceRow } from "../EventArtistBookingDrawer/performanceRules";

export interface EventArtistDatesEditorProps {
  /** The event's dates (recurring) or sessions — the ones a set can be on. */
  dates: EventTimeSlot[];
  timezone: string | null;
  isRecurring: boolean;
  /** The event's map elements, as stage options. */
  stages: Array<{ value: string; label: string }>;
  value: PerformanceRow[];
  onChange: (rows: PerformanceRow[]) => void;
  disabled?: boolean;
  error?: React.ReactNode;
}

/** A date's label: its name, else when it is, in the event's zone. */
export const dateOptionLabel = (s: EventTimeSlot, tz: string | null): string =>
  [s.name?.trim(), formatEventDateTimeRange(s.startsAt, s.endsAt, tz)].filter(Boolean).join(" · ") || "—";

export const EventArtistDatesEditor: React.FC<EventArtistDatesEditorProps> = ({
  dates,
  timezone,
  isRecurring,
  stages,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation(["artists", "common"]);

  const update = (i: number, patch: Partial<PerformanceRow>) => onChange(value.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  const remove = (i: number) => onChange(value.filter((_row, j) => j !== i));
  const add = () => onChange([...value, { eventTimeSlotId: "", start: "", end: "", mapElementId: "" }]);

  const taken = new Set(value.map((r) => r.eventTimeSlotId).filter(Boolean));
  const dateOptions = (current: string) =>
    dates
      .filter((s) => !!s.id && (s.id === current || !taken.has(s.id)))
      .map((s) => ({ value: s.id as string, label: dateOptionLabel(s, timezone) }));

  return (
    <Stack gap="sm">
      <Text size="md" c="dimmed">
        {isRecurring
          ? t("artists:booking.dates.hintRecurring", {
              defaultValue: "The dates this act plays. Leave a time empty to use the date's own time. For other terms on another date, copy the date's lineup from the Artists page.",
            })
          : t("artists:booking.dates.hint", { defaultValue: "The sessions this act plays. Leave a time empty to use the session's own time." })}
      </Text>

      {value.map((row, i) => (
        <Paper key={`${row.eventTimeSlotId}-${i}`} withBorder radius="md" p="sm">
          <Stack gap="xs">
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <SelectField
                size="md"
                style={{ flex: 1, minWidth: 0 }}
                label={isRecurring ? t("artists:booking.dates.date", { defaultValue: "Date" }) : t("artists:booking.dates.session", { defaultValue: "Session" })}
                placeholder={isRecurring ? t("artists:booking.dates.pickDate", { defaultValue: "Pick a date" }) : t("artists:booking.dates.pickSession", { defaultValue: "Pick a session" })}
                data={dateOptions(row.eventTimeSlotId)}
                value={row.eventTimeSlotId || null}
                onChange={(_n, v) => update(i, { eventTimeSlotId: (v as string) ?? "" })}
                searchable
                allowDeselect={false}
                disabled={disabled}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                size="lg"
                mt={28}
                disabled={disabled}
                aria-label={t("artists:booking.dates.remove", { defaultValue: "Take off this date" })}
                onClick={() => remove(i)}
              >
                <AppIcon iconName="X" size={18} />
              </ActionIcon>
            </Group>
            <Group gap="xs" grow align="flex-start">
              <TimeInput
                size="md"
                label={t("artists:booking.dates.start", { defaultValue: "Set starts" })}
                value={row.start}
                onChange={(e) => {
                  const next = e.currentTarget.value;

                  update(i, { start: next, ...(next ? {} : { end: "" }) });
                }}
                disabled={disabled || !row.eventTimeSlotId}
              />
              <TimeInput
                size="md"
                label={t("artists:booking.dates.end", { defaultValue: "Set ends" })}
                value={row.end}
                onChange={(e) => {
                  const next = e.currentTarget.value;

                  update(i, { end: next });
                }}
                disabled={disabled || !row.start}
              />
            </Group>
            {stages.length > 0 && (
              <SelectField
                size="md"
                label={t("artists:booking.dates.stage", { defaultValue: "Stage (on the event map)" })}
                placeholder={t("artists:booking.dates.stagePh", { defaultValue: "The date's own" })}
                data={stages}
                value={row.mapElementId || null}
                onChange={(_n, v) => update(i, { mapElementId: (v as string) ?? "" })}
                clearable
                searchable
                disabled={disabled}
              />
            )}
          </Stack>
        </Paper>
      ))}

      {error ? <Input.Error size="md">{error}</Input.Error> : null}

      <Group>
        <Button
          variant="light"
          size="md"
          leftSection={<AppIcon iconName="Plus" size={16} />}
          onClick={add}
          disabled={disabled || taken.size >= dates.length}
        >
          {isRecurring ? t("artists:booking.dates.add", { defaultValue: "Add a date" }) : t("artists:booking.dates.addSession", { defaultValue: "Add a session" })}
        </Button>
      </Group>
    </Stack>
  );
};
